import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useLoader } from '../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import SEO from '../../../component/common/SEO';
import { generateProductSEO } from '../../../config/seoConfig';
import { PDP_VALUES, PRICE_LOGIC, SellingModes } from '../../../helper/constants';
import { fetchLocalShippingCost, isVolumetricWeigthExtraLarge } from '../../../helper/utils';
import { fetchCartItems } from '../../../redux/slices/cartSlice';
import { ProductsApiService } from '../../../service/website/productsApiService';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import BreadCrumbs from '../../component/common/breadCrumbs';
import OrderSummary from '../../component/views/productDetails/OrderSummary';
import ProductDetailLeft from '../../component/views/productDetails/ProductDetailLeft';
import ProductDetailRight from '../../component/views/productDetails/ProductDetailRight.tsx';
import { calculateDropshippingPrice, getTotalSelectedQuantity, getPriceForQuantity, appendSizeToImageUrl, getProductShippingInfoForPdp, enrichVariantPhysicalsFromProductShipping } from '../../component/views/productDetails/utils';
import logger from '../../helper/logger';
import { CartApiService } from '../../service/cartApiService';
import SolrService from '../../../service/solr/solrService';
import './style.css';
import CentralizePriceCalculation from '../../../service/pricing/pricingService';
import { TranslationService } from '../../../service/translate/translationService';
import { parseProductUrlParams, isLegacyUrl, generateProductUrl } from '../../../utils/productUrlUtils';
import { SearchLogService } from '../../../service/admin/searchLogService';
import { fetchToken } from '../../../lib/globalApiClient';


// If Wholesale's starting quantity is 1 then make it 2.
// This 2 initial-start-quantity is static for now, but admin will set it dynamically.
const WholesaleInitialQuantity = PDP_VALUES.WHOLESALE_INITIAL_MIN_QUANTITY;

// Helper to check if dropshipping is available for a product
const isDropshippingAvailableFromTags = (tagInfoList = []) => {
  const isOnePsaleTag = tagInfoList.find(tag => tag.key === 'isOnePsale');
  // Dropshipping is available if isOnePsale is not false
  return !(isOnePsaleTag?.value === false || isOnePsaleTag?.value === 'false');
};

/**
 * Calculate chargeable weight for a variant based on weight/dimension scenarios
 * Keeps original values for display, adds chargeableWeight for pricing
 * @param {object|null} product - when set, if variant weight+dims are zero, use productShippingInfo (API) / globalApiResponse (DB) parent fields
 */
const calculateVariantChargeableWeight = (variant, product = null) => {
  let rawWeight = variant.weight || 0;
  let rawLength = variant.length || 0;
  let rawHeight = variant.height || 0;
  let rawWidth = variant.width || 0;
  if (product) {
    const e = enrichVariantPhysicalsFromProductShipping(product, rawWeight, rawLength, rawHeight, rawWidth);
    rawWeight = e.rawWeight;
    rawLength = e.rawLength;
    rawHeight = e.rawHeight;
    rawWidth = e.rawWidth;
  }

  const hasDimensions = rawLength > 0 && rawHeight > 0 && rawWidth > 0;
  const hasWeight = rawWeight > 0;

  const defaultWeight = PRICE_LOGIC.DEFAULT_DW || 0.5;
  const volumeFactor = PRICE_LOGIC.VOLUME_FACTOR || 5000;
  const weightBufferPercent = PRICE_LOGIC.WEIGHT_BUFFER_PERCENT || 0.05;

  const volumetricWeight = hasDimensions ? (rawLength * rawWidth * rawHeight) / volumeFactor : 0;

  // Determine chargeable weight:
  // 1. Both missing - use default weight
  // 2. Only weight available (dimensions missing) - assign volumetricWeight = defaultWeight, then max(actualWeight, defaultWeight)
  // 3. Only dimensions available (weight missing) - use max(volumetricWeight, defaultWeight)
  // 4. Both available - use max(actualWeight, volumetricWeight)
  let chargeableWeight;
  let effectiveVolumetricWeight = volumetricWeight;

  if (!hasDimensions && !hasWeight) {
    chargeableWeight = defaultWeight;
  } else if (hasWeight && !hasDimensions) {
    // Only weight available - assign volumetricWeight = defaultWeight, then compare
    effectiveVolumetricWeight = defaultWeight;
    chargeableWeight = Math.max(rawWeight, effectiveVolumetricWeight);
  } else if (hasDimensions && !hasWeight) {
    chargeableWeight = Math.max(volumetricWeight, defaultWeight);
  } else {
    chargeableWeight = Math.max(rawWeight, volumetricWeight);
  }

  // Note: Buffer weight is applied in the backend pricing API, not here

  return {
    ...variant,
    weight: rawWeight,
    length: rawLength,
    height: rawHeight,
    width: rawWidth,
    chargeableWeight,
  };
};

const ProductDetails = () => {
  const loader = useLoader();
  const message = useMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authData = getAuthLocalStorage();
  const isLoggedIn = authData.isLoggedIn && authData.loginHeader && (authData.userType === 'seller' || authData.userType === 'admin' || authData.userType === 'sub-admin');

  const [searchParams] = useSearchParams();
  const routeParams = useParams();

  // Support both legacy query params and new SEO-friendly URL format
  // Legacy: /product-details/?id=xxx or /product-details/?ali=1&offer=xxx
  // New: /{skuID}/{variantId}/{slug} or /{skuID}/{slug}
  const parsedParams = useMemo(() => {
    if (routeParams.skuId) {
      // New SEO-friendly URL format
      return parseProductUrlParams(routeParams);
    }
    // Legacy query params format
    return {
      id: searchParams.get('id'),
      ali: searchParams.get('ali'),
      offer: searchParams.get('offer'),
      variantId: null,
    };
  }, [routeParams, searchParams]);

  const id = parsedParams.id;
  const ali = parsedParams.ali;
  const offer = parsedParams.offer;
  const initialVariantId = parsedParams.variantId;

  const [product, setProduct] = useState({});
  const [isDbProduct, setIsDbProduct] = useState(false);
  const [aliDetail, setAliDetail] = useState({});
  const [priceRanges, setPriceRanges] = useState([]);
  const [preferredImage, setPreferredImage] = useState('');
  const [pdLoading, setPdLoading] = useState(false);
  const [variantDetails, setVariantDetails] = useState({}); // Store details for each variant
  const [actualVariants, setActualVariants] = useState({}); // Store actual variants from Alibaba
  const [markupPercentValue, setMarkupPercentValue] = useState(0); // Store details for each variant
  const [selectedVariants, setSelectedVariants] = useState({}); // Track all selected variants with quantities
  const [pricingMode, setPricingMode] = useState(SellingModes.Wholesale);
  const [variantPrices, setVariantPrices] = useState({}); // Store calculated dropshipping prices
  const [totalWholesalePrice, setTotalWholesalePrice] = useState(0);
  const [MOQ, setMOQ] = useState();
  // Initialize customMOQ with MOQ from API if available, otherwise use WholesaleInitialQuantity
  const [customMOQ, setCustomMOQ] = useState(WholesaleInitialQuantity);
  const [pricingData, setPricingData] = useState(null); // Store wholesale pricing breakdown
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [hasFetchedPricing, setHasFetchedPricing] = useState(false);
  const [autoUpdating, setAutoUpdating] = useState(false);
  const [productError, setProductError] = useState(null); // Store product error message
  const [askForPricingQuotation, setAskForPricingQuotation] = useState(false);
  const [isOpenAskInquiry, setIsOpenAskInquiry] = useState(false);

  // Price logs state - for products with DB variant prices
  const [hasPriceLogs, setHasPriceLogs] = useState(false);
  const [dbVariantPrices, setDbVariantPrices] = useState(null);

  // Check if weight or volumetric weight exceeds 5kg
  const shouldShowWeightMessage = useMemo(() => {
    const volumeFactor = PRICE_LOGIC.VOLUME_FACTOR || 5000;
    const weightThreshold = 5; // 5kg

    if (!product && (!variantDetails || Object.keys(variantDetails).length === 0)) {
      return false;
    }

    // Check product weight and dimensions (include productShippingInfo / globalApiResponse fallbacks)
    if (product) {
      const ep = enrichVariantPhysicalsFromProductShipping(
        product,
        product?.weight || 0,
        typeof product?.length === 'string' ? parseFloat(product.length) : product?.length || 0,
        typeof product?.height === 'string' ? parseFloat(product.height) : product?.height || 0,
        typeof product?.width === 'string' ? parseFloat(product.width) : product?.width || 0
      );
      const productWeight = ep.rawWeight || 0;
      const productLength = ep.rawLength || 0;
      const productWidth = ep.rawWidth || 0;
      const productHeight = ep.rawHeight || 0;

      const productVolumetricWeight = productLength && productWidth && productHeight
        ? (productLength * productWidth * productHeight) / volumeFactor
        : 0;

      if (productVolumetricWeight > productWeight * 5) {
        return true;
      }
    }

    // Check all variants
    if (variantDetails && Object.keys(variantDetails).length > 0) {
      const variants = Object.values(variantDetails);
      for (const variant of variants) {
        const ev = enrichVariantPhysicalsFromProductShipping(product, variant?.weight, variant?.length, variant?.height, variant?.width);
        const variantWeight = ev.rawWeight || 0;
        const variantLength = ev.rawLength || 0;
        const variantWidth = ev.rawWidth || 0;
        const variantHeight = ev.rawHeight || 0;

        const variantVolumetricWeight = variantLength && variantWidth && variantHeight
          ? (variantLength * variantWidth * variantHeight) / volumeFactor
          : 0;

        if (variantVolumetricWeight > variantWeight * 5) {
          return true;
        }
      }
    }

    return false;
  }, [product, variantDetails]);

  // If selected variant(s) lack weight or dimensions, show Send Inquiry only — no price, no Add to cart.
  // When product has variants: only the selected variant(s) must have both weight and dimensions to show price.
  // const hasZeroDimensions = useMemo(() => {
  //   const parseNum = (v) => (v === undefined || v === null ? 0 : (typeof v === 'string' ? parseFloat(v) : Number(v)) || 0);

  //   if (!product && (!variantDetails || Object.keys(variantDetails).length === 0)) {
  //     return false;
  //   }

  //   // Product without variants: use product-level weight/dimensions
  //   if (product && (!variantDetails || Object.keys(variantDetails).length === 0)) {
  //     const w = parseNum(product.weight);
  //     const h = parseNum(product.height);
  //     const l = parseNum(product.length);
  //     const wd = parseNum(product.width);
  //     if (w === 0 || h === 0 || l === 0 || wd === 0) return true;
  //     return false;
  //   }

  //   // Product with variants: check if any variant has full dimensions/weight
  //   if (variantDetails && Object.keys(variantDetails).length > 0) {
  //     const selectedEntries = Object.entries(selectedVariants).filter(([, v]) => v && v.quantity > 0);

  //     // No variant selected → check if ANY variant in the product has non-zero weight & dimensions
  //     if (selectedEntries.length === 0) {
  //       const anyVariantHasDimensions = Object.values(variantDetails).some(variant => {
  //         const w = parseNum(variant?.weight);
  //         const h = parseNum(variant?.height);
  //         const l = parseNum(variant?.length);
  //         const wd = parseNum(variant?.width);
  //         return w !== 0 && h !== 0 && l !== 0 && wd !== 0;
  //       });
  //       return !anyVariantHasDimensions;
  //     }

  //     // Any selected variant missing weight or dimensions → show Send Inquiry
  //     for (const [skuId, selected] of selectedEntries) {
  //       const variant = variantDetails[skuId] || selected;
  //       const w = parseNum(variant?.weight);
  //       const h = parseNum(variant?.height);
  //       const l = parseNum(variant?.length);
  //       const wd = parseNum(variant?.width);
  //       if (w === 0 || h === 0 || l === 0 || wd === 0) return true;
  //     }
  //     return false;
  //   }

  //   return false;
  // }, [product, variantDetails, selectedVariants]);

  // Require inquiry (hide price, show Send Inquiry) only when weight or dimension is 0.
  // Sync with hasZeroDimensions in both directions so when variants load with dimensions we show price again.
  // useEffect(() => {
  //   setAskForPricingQuotation(!!hasZeroDimensions);
  // }, [hasZeroDimensions]);

  const loadingVariantsRef = useRef(new Set()); // Track variants being calculated
  const abortControllersRef = useRef(new Map()); // Track abort controllers for pricing requests
  const variantPricesRef = useRef({}); // Ref to track variantPrices without causing re-renders
  const initialPricingTriggeredRef = useRef(false); // Track if initial pricing calculations have been triggered
  const pricingAbortControllerRef = useRef(null); // Track abort controller for wholesale pricing
  const pdpTrackedRef = useRef(null); // Track if PDP open has been tracked for current product
  const lastFetchTimeRef = useRef(0); // Track last fetch time to prevent duplicate calls

  const totalSelectedVaraintsQuantity = useMemo(() => getTotalSelectedQuantity(selectedVariants), [selectedVariants]);
  const isWholesale = pricingMode === SellingModes.Wholesale;
  const isDropshippingAvailable = useMemo(() => isDropshippingAvailableFromTags(product?.tagInfoList || []), [product?.tagInfoList]);
  const effectiveWholesaleMOQ = useMemo(() => {
    const parseQty = value => {
      const num = Number(value);
      return Number.isFinite(num) && num > 0 ? num : 0;
    };

    // Prefer the strictest MOQ so cart validation never allows under-MOQ checkout.
    const firstRangeMOQ = parseQty(priceRanges?.[0]?.startQuantity ?? priceRanges?.[0]?.start_quantity);
    const candidates = [
      parseQty(customMOQ),
      parseQty(MOQ),
      parseQty(product?.moq),
      parseQty(product?.minOrderQuantity),
      parseQty(product?.min_order_quantity),
      parseQty(product?.productSaleInfo?.minOrderQuantity),
      firstRangeMOQ,
    ].filter(Boolean);

    const result = candidates.length ? Math.max(...candidates) : 0;

    // // Debug logging for MOQ calculation
    // console.log('MOQ Calculation Debug:', {
    //   customMOQ,
    //   MOQ,
    //   productMoq: product?.moq,
    //   productMinOrderQuantity: product?.minOrderQuantity,
    //   productMin_order_quantity: product?.min_order_quantity,
    //   productSaleInfoMinOrderQuantity: product?.productSaleInfo?.minOrderQuantity,
    //   firstRangeMOQ,
    //   candidates,
    //   effectiveWholesaleMOQ: result
    // });

    return result;
  }, [customMOQ, MOQ, product, priceRanges]);

  // Wholesale MOQ for add-to-cart / buy-now only:
  // - One-piece available → effectiveWholesaleMOQ (existing logic, e.g. min 2 when wholesale MOQ is 1).
  // - One-piece not available → actual product wholesale MOQ only (no min-2 bump from tiers/customMOQ).
  const wholesaleMOQToEnforce =
    isDropshippingAvailable
      ? effectiveWholesaleMOQ
      : Number(MOQ || product?.moq || product?.minOrderQuantity || 0);
      
  // Keep ref in sync with state
  useEffect(() => {
    variantPricesRef.current = variantPrices;
  }, [variantPrices]);

  // Track PDP open event - track once when product loads
  useEffect(() => {
    if (product && Object.keys(product).length > 0 && (product.offerId || product.sku || product._id)) {
      const productId = product.offerId || product.sku || product._id;
      const currentProductId = String(productId);

      // Only track if this is a different product than last tracked
      if (productId && pdpTrackedRef.current !== currentProductId) {
        pdpTrackedRef.current = currentProductId; // Mark as tracked for this product
        // Track asynchronously (don't wait for it)
        SearchLogService.trackPdpOpen(currentProductId)
          .then(response => {
            // console.log('[PDP Tracking] Success:', response);
          })
          .catch(err => {
            // console.error('[PDP Tracking] Failed to track PDP open:', err);
          });
      }
    }
  }, [product?.offerId, product?.sku, product?._id]);

  // Update browser URL to SEO-friendly format when product loads (display only, no navigation)
  useEffect(() => {
    if (product && (product.title || product.name) && (product.offerId || product._id || product.sku)) {
      // Use offerId for sku/ path segment
      const offerId = product.offerId || product._id || product.sku;
      const isAliProduct = ali === '1' || (!product._id && (product.sku || product.offerId));

      // Use initialVariantId from URL if available and variant exists, otherwise use first variant's skuId
      let variantIdForUrl = null;

      // Check if initialVariantId exists in variantDetails
      if (initialVariantId && initialVariantId !== '0' && variantDetails && Object.keys(variantDetails).length > 0) {
        // Check if variant exists by direct key or by searching
        const variantExists = variantDetails[initialVariantId] ||
          Object.values(variantDetails).some(v => String(v.skuId) === String(initialVariantId));
        if (variantExists) {
          variantIdForUrl = initialVariantId;
        }
      }

      // Fallback to first variant if no valid initialVariantId
      if (!variantIdForUrl && variantDetails && Object.keys(variantDetails).length > 0) {
        const firstVariant = Object.values(variantDetails)[0];
        variantIdForUrl = firstVariant?.skuId ? String(firstVariant.skuId) : null;
      }

      // Only generate URL if we have a valid variantId (skip if variants not loaded yet)
      if (!variantIdForUrl && variantDetails && Object.keys(variantDetails).length === 0) {
        return; // Wait for variantDetails to load
      }

      const newUrl = generateProductUrl({
        id: !isAliProduct ? offerId : null,
        offer: isAliProduct ? offerId : null,
        isAli: isAliProduct,
        title: product.title || product.name,
        variantId: variantIdForUrl,
        search: searchParams.get('search'),
      });

      // Only update if URL is different (avoid unnecessary history entries)
      if (location.pathname + location.search !== newUrl) {
        window.history.replaceState(null, '', newUrl);
      }
    }
  }, [product, variantDetails, ali, searchParams, location.pathname, location.search, initialVariantId]);

  // Callback to update URL when variant is selected
  const handleVariantUrlUpdate = useCallback((variantSkuId) => {
    if (product && (product.title || product.name) && (product.offerId || product._id || product.sku)) {
      const offerId = product.offerId || product._id || product.sku;
      const isAliProduct = ali === '1' || (!product._id && (product.sku || product.offerId));

      const newUrl = generateProductUrl({
        id: !isAliProduct ? offerId : null,
        offer: isAliProduct ? offerId : null,
        isAli: isAliProduct,
        title: product.title || product.name,
        variantId: variantSkuId || '0',
        search: searchParams.get('search'),
      });

      window.history.replaceState(null, '', newUrl);
    }
  }, [product, ali, searchParams]);

  // Group variants by attributes and compute price extremes
  // Calculate separate min/max for wholesale (wholesaleFinalPrice) and dropshipping (finalPrice)
  const { sortedAttributeGroups, maxVariantPrice, minVariantPrice, maxWholesalePrice, minWholesalePrice, groupedVariants, maxVriantWeight, maxVariantVolume } = useMemo(() => {
    if (!variantDetails || Object.keys(variantDetails).length === 0) {
      return { sortedAttributeGroups: [], maxVariantPrice: null, minVariantPrice: null, maxWholesalePrice: null, minWholesalePrice: null, groupedVariants: new Map() };
    }
    const grouped = new Map();
    const attributeGroups = {};

    let maxPrice = 0;      // Dropshipping max
    let minPrice = Infinity; // Dropshipping min
    let maxWsPrice = 0;    // Wholesale max
    let minWsPrice = Infinity; // Wholesale min

    Object.values(variantDetails).forEach(variant => {
      // Dropshipping price: use calculatedFBP if available, otherwise fallback to existing logic
      const dropshippingPrice = variant.calculatedFBP || variantPrices[variant.skuId]?.finalPrice || variant?.finalPrice;
      const priceCandidate = parseFloat(
        dropshippingPrice || variant.fenxiaoPriceInfo?.onePiecePrice || variant.fenxiaoPriceInfo?.offerPrice || variant?.consignPrice || variant?.price || '0'
      );
      maxPrice = Math.max(maxPrice, priceCandidate);
      if (priceCandidate > 0) minPrice = Math.min(minPrice, priceCandidate);

      // Wholesale price: use calculatedFBP if available, otherwise fallback to existing logic
      const wholesalePrice = variant.calculatedFBP || variant?.wholesaleFinalPrice || variant?.finalPrice;
      const wsPriceCandidate = parseFloat(
        wholesalePrice || variant?.consignPrice || variant.fenxiaoPriceInfo?.offerPrice || variant?.price || '0'
      );
      maxWsPrice = Math.max(maxWsPrice, wsPriceCandidate);
      if (wsPriceCandidate > 0) minWsPrice = Math.min(minWsPrice, wsPriceCandidate);

      let uniqueAttrCombinationStr = '';
      variant.skuAttributes?.forEach(attr => {
        uniqueAttrCombinationStr += attr.valueTrans || attr.value;

        const attrId = attr.attributeId;
        const attrNameTrans = attr.attributeNameTrans || attr.attributeName;
        const attrName = attr.attributeName;

        if (!attributeGroups[attrId]) {
          attributeGroups[attrId] = {
            id: attrId,
            nameTrans: attrNameTrans,
            name: attrName,
            isColor: String(attrNameTrans || '').toLowerCase().includes('color') || String(attrName || '').toLowerCase().includes('颜色'),
            values: new Map(),
          };
        }

        const valueKey = attr.valueTrans || attr.value;
        if (!attributeGroups[attrId].values.has(valueKey)) {
          attributeGroups[attrId].values.set(valueKey, {
            valueTrans: attr.valueTrans,
            value: attr.value,
            imageUrl: attr.skuImageUrl,
            variants: [],
          });
        }

        attributeGroups[attrId].values.get(valueKey).variants.push(variant);
      });
      grouped.set(uniqueAttrCombinationStr, variant);
    });

    const sortedGroups = Object.values(attributeGroups).sort((a, b) => {
      if (a.isColor && !b.isColor) return -1;
      if (!a.isColor && b.isColor) return 1;
      return 0;
    });

    return {
      sortedAttributeGroups: sortedGroups,
      maxVariantPrice: maxPrice,                              // Dropshipping max
      minVariantPrice: minPrice === Infinity ? null : minPrice, // Dropshipping min
      maxWholesalePrice: maxWsPrice,                          // Wholesale max
      minWholesalePrice: minWsPrice === Infinity ? null : minWsPrice, // Wholesale min
      groupedVariants: grouped,
    };
  }, [variantDetails, variantPrices]);

  // When only one variant has weight/dimensions, show that variant's price on top
  const { singleVariantPriceForTop, showSingleVariantPriceOnTop } = useMemo(() => {
    if (!variantDetails || Object.keys(variantDetails).length === 0) return { singleVariantPriceForTop: null, showSingleVariantPriceOnTop: false };
    const parseNum = (v) => (v === undefined || v === null ? 0 : (typeof v === 'string' ? parseFloat(v) : Number(v)) || 0);
    const withDimensions = Object.entries(variantDetails).filter(([, v]) => {
      const e = enrichVariantPhysicalsFromProductShipping(product, v?.weight, v?.length, v?.height, v?.width);
      const w = parseNum(e.rawWeight);
      const h = parseNum(e.rawHeight);
      const l = parseNum(e.rawLength);
      const wd = parseNum(e.rawWidth);
      return w > 0 && h > 0 && l > 0 && wd > 0;
    });
    if (withDimensions.length !== 1) return { singleVariantPriceForTop: null, showSingleVariantPriceOnTop: false };
    const [skuId, variant] = withDimensions[0];
    const dropshippingPrice = parseFloat(variantPrices[skuId]?.finalPrice ?? variant?.finalPrice ?? 0) || null;
    const wholesalePrice = parseFloat(variant?.wholesaleFinalPrice ?? variant?.finalPrice ?? variant?.consignPrice ?? variant?.fenxiaoPriceInfo?.offerPrice ?? variant?.price ?? 0) || null;
    const price = dropshippingPrice ?? wholesalePrice;
    return { singleVariantPriceForTop: price, showSingleVariantPriceOnTop: price != null && price > 0 };
  }, [variantDetails, variantPrices, product]);

  // At least one variant has both a price (> 0) and non-zero weight/dimensions → show price instead of red "send inquiry" text
  const hasVariantsWithPriceAndDimensions = useMemo(() => {
    if (!variantDetails || Object.keys(variantDetails).length === 0) return false;
    const parseNum = (v) => (v === undefined || v === null ? 0 : (typeof v === 'string' ? parseFloat(v) : Number(v)) || 0);
    for (const variant of Object.values(variantDetails)) {
      const e = enrichVariantPhysicalsFromProductShipping(product, variant?.weight, variant?.length, variant?.height, variant?.width);
      const w = parseNum(e.rawWeight);
      const h = parseNum(e.rawHeight);
      const l = parseNum(e.rawLength);
      const wd = parseNum(e.rawWidth);
      if (w === 0 || h === 0 || l === 0 || wd === 0) continue;
      const dropshippingPrice = parseFloat(variantPrices[variant.skuId]?.finalPrice ?? variant?.finalPrice ?? 0) || 0;
      const wholesalePrice = parseFloat(variant?.wholesaleFinalPrice ?? variant?.finalPrice ?? variant?.consignPrice ?? variant?.fenxiaoPriceInfo?.offerPrice ?? variant?.price ?? 0) || 0;
      const hasPrice = dropshippingPrice > 0 || wholesalePrice > 0;
      if (hasPrice) return true;
    }
    return false;
  }, [variantDetails, variantPrices, product]);

  const isMinMaxRange = minVariantPrice !== maxVariantPrice;
  // Calculate dropshipping price for a variant when it becomes visible
  const calculateVariantPrice = useCallback(
    async skuId => {
      const variant = actualVariants[skuId];
      // Use ref to check if price already exists (avoids dependency on variantPrices)
      if (!variant || loadingVariantsRef.current.has(skuId) || variantPricesRef.current[skuId]) return;
      if (ali !== '1' || pricingMode !== SellingModes.DropShipping) return;
      // Ensure product and offerId/sku are available
      if (!product || (!product.offerId && !product.sku)) return;

      loadingVariantsRef.current.add(skuId);
      const controller = new AbortController();
      abortControllersRef.current.set(skuId, controller);

      // Merge live API shipping into product so getProductShippingInfoForPdp sees it; DB still uses globalApiResponse
      const productForShipping = {
        ...product,
        ...(aliDetail?.productShippingInfo
          ? {
            productShippingInfo: aliDetail.productShippingInfo,
            detailed_info: { ...product.detailed_info, productShippingInfo: aliDetail.productShippingInfo },
          }
          : {}),
      };
      const pShip = getProductShippingInfoForPdp(productForShipping);
      const skuShippingDetails = pShip?.skuShippingDetails || [];
      const shippingDetail = skuShippingDetails.find(detail =>
        String(detail.skuId) === String(skuId) || detail.skuId === skuId
      );

      let dw = shippingDetail?.weight || variant.weight || 0;
      let dl = shippingDetail?.length || variant.length || 0;
      let dh = shippingDetail?.height || variant.height || 0;
      let dwd = shippingDetail?.width || variant.width || 0;
      const enriched = enrichVariantPhysicalsFromProductShipping(productForShipping, dw, dl, dh, dwd);
      dw = enriched.rawWeight;
      dl = enriched.rawLength;
      dh = enriched.rawHeight;
      dwd = enriched.rawWidth;
      const variantDW = dw;
      const variantVW =
        dl > 0 && dwd > 0 && dh > 0
          ? (dl * dwd * dh) / PRICE_LOGIC.VOLUME_FACTOR
          : 0;
      const isSendPriceQuery = isVolumetricWeigthExtraLarge(variantDW, variantVW);
      if (isSendPriceQuery && !askForPricingQuotation) setAskForPricingQuotation(!!isSendPriceQuery);

      // Pass product with detailed_info.productShippingInfo for calculateDropshippingPrice
      const productWithShippingInfo = {
        ...product,
        detailed_info: {
          ...product.detailed_info,
          productShippingInfo: aliDetail?.productShippingInfo || product.detailed_info?.productShippingInfo,
        },
        productShippingInfo: aliDetail?.productShippingInfo || product.productShippingInfo,
        topCategoryId: aliDetail?.topCategoryId || product?.topCategoryId || '',
        secondCategoryId: aliDetail?.secondCategoryId || product?.secondCategoryId || '',
        thirdCategoryId: aliDetail?.thirdCategoryId || product?.thirdCategoryId || '',
      };
      const priceData = await calculateDropshippingPrice(variant, productWithShippingInfo, controller.signal);

      if (priceData && !controller.signal.aborted) {
        setVariantPrices(prev => ({ ...prev, [skuId]: { finalPrice: priceData.finalPrice, breakdown: priceData.breakdown } }));
      }

      loadingVariantsRef.current.delete(skuId);
      abortControllersRef.current.delete(skuId);
    },
    [actualVariants, product, ali, pricingMode, aliDetail, askForPricingQuotation]
  );

  // Trigger initial price calculations when product and variantDetails are loaded
  // Skip if prices are already pre-calculated (from mapAlibabaVariations)
  useEffect(() => {
    if (
      ali === '1' &&
      pricingMode === SellingModes.DropShipping &&
      product &&
      (product.offerId || product.sku) &&
      Object.keys(variantDetails).length > 0 &&
      !initialPricingTriggeredRef.current
    ) {
      // Check if prices are already pre-calculated
      const hasPreCalculatedPrices = Object.keys(variantPrices).length > 0 &&
        Object.values(variantPrices).some(vp => vp?.finalPrice > 0);

      if (hasPreCalculatedPrices) {
        // Prices already pre-calculated, no need to trigger on-demand calculations
        initialPricingTriggeredRef.current = true;
        return;
      }

      initialPricingTriggeredRef.current = true;

      // Clear existing prices to recalculate
      setVariantPrices({});
      loadingVariantsRef.current.clear();

      // Trigger calculations progressively: first 10 immediately, then remaining after 2.5 seconds
      const skuIds = Object.keys(variantDetails);
      const firstBatch = skuIds.slice(0, 10);
      const remainingBatch = skuIds.slice(10);

      // Load first 10 variants immediately with small staggered delays
      firstBatch.forEach((skuId, index) => {
        setTimeout(() => calculateVariantPrice(skuId), index * 10);
      });

      // Load remaining variants after 2.5 seconds (when first batch has rendered)
      if (remainingBatch.length > 0) {
        setTimeout(() => {
          remainingBatch.forEach((skuId, index) => {
            setTimeout(() => calculateVariantPrice(skuId), index * 10);
          });
        }, 2500); // 2.5 seconds delay
      }
    }
  }, [ali, pricingMode, product, variantDetails, variantPrices, calculateVariantPrice]);

  // Reset initial pricing trigger when pricing mode changes
  useEffect(() => {
    initialPricingTriggeredRef.current = false;
  }, [pricingMode]);

  // Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach(controller => controller.abort());
      abortControllersRef.current.clear();
      if (pricingAbortControllerRef.current) {
        pricingAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Automatically fetch pricing when variants or quantity changes (only after first manual fetch)
  useEffect(() => {
    const hasSelection = Object.values(selectedVariants).some(v => v.quantity > 0);

    console.log('[DEBUG] Auto-fetch effect triggered', {
      hasSelection,
      isWholesale,
      hasFetchedPricing,
      totalSelectedVaraintsQuantity,
      selectedVariantsKeys: Object.keys(selectedVariants),
    });

    if (isWholesale && hasFetchedPricing && hasSelection && totalSelectedVaraintsQuantity > 0) {
      // Prevent duplicate calls within 500ms
      const now = Date.now();
      if (now - lastFetchTimeRef.current < 500) {
        console.log('[DEBUG] Skipping duplicate fetch call (too soon)');
        return;
      }
      lastFetchTimeRef.current = now;

      // Clear pricing data immediately when quantity changes to avoid showing stale data
      console.log('[DEBUG] Calling fetchWholesalePricing from auto-fetch effect');
      setPricingData(null);
      setAutoUpdating(true);
      fetchWholesalePricing(true); // true indicates automatic update
    }

    // For dropshipping mode with hasPriceLogs, auto-fetch pricing when selection changes
    if (!isWholesale && hasPriceLogs && hasSelection && totalSelectedVaraintsQuantity > 0) {
      setPricingData(null);
      setAutoUpdating(true);
      fetchDropshippingPricing(true);
    }

    // Reset fetched state when selection is cleared
    if (!hasSelection) {
      setHasFetchedPricing(false);
      setPricingData(null);
      setAutoUpdating(false);
    }

    // Cancel any ongoing request when dependencies change
    return () => {
      if (pricingAbortControllerRef.current) {
        pricingAbortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariants, totalSelectedVaraintsQuantity, isWholesale, hasPriceLogs]);

  // Clear pricing data when wholesale mode changes
  useEffect(() => {
    setPricingData(null);
    setHasFetchedPricing(false);
    setAutoUpdating(false);
  }, [isWholesale]);

  // Fetch wholesale pricing breakdown from APIs
  const fetchWholesalePricing = async (isAutoUpdate = false) => {
    console.log('[DEBUG] fetchWholesalePricing called', { isAutoUpdate, timestamp: new Date().toISOString() });
    const hasSelection = Object.values(selectedVariants).some(v => v.quantity > 0);
    if (!isWholesale || !hasSelection || !totalSelectedVaraintsQuantity) {
      setPricingData(null);
      setHasFetchedPricing(false);
      setAutoUpdating(false);
      return null;
    }

    // Cancel previous request if exists
    if (pricingAbortControllerRef.current) {
      pricingAbortControllerRef.current.abort();
    }

    pricingAbortControllerRef.current = new AbortController();
    const signal = pricingAbortControllerRef.current.signal;

    if (isAutoUpdate) {
      setAutoUpdating(true);
    } else {
      setLoadingPricing(true);
    }
    setPricingData(null); // Clear previous data when fetching new

    try {
      // Build dynamic logistics SKU models from all selected variants
      const logisticsSkuNumModels = Object.values(selectedVariants)
        .filter(v => v.quantity > 0 && v.skuId)
        .map(v => ({ skuId: v.skuId, quantity: v.quantity }));
      if (!logisticsSkuNumModels.length) {
        setLoadingPricing(false);
        setAutoUpdating(false);
        return null;
      }

      // Get offerId from product (handle both DB and Alibaba products)
      const offerId = product?.offerId || product?.offer_id || product?.sku;
      if (!offerId) {
        setLoadingPricing(false);
        setAutoUpdating(false);
        return null;
      }

      // OLD CODE: Commented out - Two separate API calls
      /*
      // Fetch freight cost for CN Procurement using API service
      let shippingCost = 0;
      const freightResult = await ProductsApiService.getLocalShippingCost(parseFloat(offerId), logisticsSkuNumModels, signal);

      if (signal.aborted) return null;

      if (freightResult.success) {
        shippingCost = freightResult.shippingCost;
      }

      // Use first variant's weight, fallback to product weight
      // Use chargeableWeight for pricing calculations (includes max logic + buffer)
      const { totalVariantsBasePrice, totalVariantsWeight, totalVariantsVolume, totalVariants } = Object.values(selectedVariants).reduce(
        (sum, v) => {
          if (v.quantity > 0) {
            const originalPrice = parseFloat(v.cp || v.consignPrice || v.ap || v.fenxiaoPriceInfo?.onePiecePrice || v.fenxiaoPriceInfo?.offerPrice || v.price || 0);
            let priceToUse = originalPrice;
            // Apply quantity-based discounts from priceRanges for wholesale mode
            // This applies to ALL products (with or without price logs) to ensure discounts work
            if (priceRanges?.length > 0) {
              if (!isMinMaxRange && isWholesale && priceRanges?.length > 0 && totalSelectedVaraintsQuantity > 0) {
                const rangePrice = getPriceForQuantity(originalPrice, totalSelectedVaraintsQuantity, priceRanges);
                if (rangePrice !== null) {
                  priceToUse = rangePrice.price;
                } else {
                  priceToUse = priceRanges[0].price;
                }
              }
            }
            return {
              totalVariants: sum.totalVariants + v.quantity,
              totalVariantsBasePrice: sum.totalVariantsBasePrice + priceToUse * (v.quantity || 1),
              // Use chargeableWeight for pricing if available, otherwise use weight
              totalVariantsWeight: sum.totalVariantsWeight + (v.chargeableWeight || v.weight || 0) * (v.quantity || 1),
              totalVariantsVolume: sum.totalVariantsVolume + (v.height && v.width && v.length ? v.height * v.width * v.length * (v.quantity || 1) : 0),
            };
          }
          return sum;
        },
        { totalVariantsBasePrice: 0, totalVariantsWeight: 0, totalVariantsVolume: 0, totalVariants: 0 }
      );

      // IMPORTANT: To ensure discounts work the same for products with and without price logs:
      // - When quantity > 1: ALWAYS use forward calculation with discounted AP (NEVER pass dbFinalPrice)
      // - When quantity = 1: Can use reverse calculation if price logs exist (for DB price accuracy)
      // This ensures quantity discounts always apply correctly, regardless of price logs

      let shouldUseReverseCalculation = false;
      let totalDbFinalPrice = 0;

      // Simple rule: Only use reverse calculation when quantity is exactly 1
      // For quantity > 1, always use forward calculation to ensure discounts apply
      if (hasPriceLogs && dbVariantPrices && totalSelectedVaraintsQuantity === 1) {
        // Quantity is 1 - can use reverse calculation for DB price accuracy
        Object.values(selectedVariants).forEach(v => {
          if (v.quantity > 0 && dbVariantPrices[v.skuId]) {
            const dbVariant = dbVariantPrices[v.skuId];
            // Use wholesaleFinalPrice for wholesale mode
            const dbPrice = parseFloat(dbVariant.wholesaleFinalPrice || dbVariant.finalPrice || 0);
            totalDbFinalPrice += dbPrice * v.quantity;
          }
        });
        shouldUseReverseCalculation = totalDbFinalPrice > 0;
      }
      // For quantity > 1: Always use forward calculation (don't pass dbFinalPrice)
      // This ensures discounts work exactly the same way as products without price logs

      const calculationData = {
        ap: totalVariantsBasePrice,
        ls: shippingCost,
        hsn: null,
        hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
        dw: totalVariantsWeight > 0 ? totalVariantsWeight : (PRICE_LOGIC.DEFAULT_DW || 0.5) * totalVariants,
        vw: totalVariantsVolume / PRICE_LOGIC.VOLUME_FACTOR,
        categoryId: product?.categoryId || '',
        topCategoryId: product?.topCategoryId || '',
        secondCategoryId: product?.secondCategoryId || '',
        thirdCategoryId: product?.thirdCategoryId || '',
        productShippingInfo: product?.productShippingInfo || product?.productShippingInfo || {},
        useDw: !isAutoUpdate, // true if called from button, false if auto-update
        // Only pass dbFinalPrice when quantity is exactly 1 (no discount scenario)
        // For quantity > 1, NEVER pass dbFinalPrice to ensure discounts apply correctly
        ...(shouldUseReverseCalculation && totalDbFinalPrice > 0 && totalSelectedVaraintsQuantity === 1 ? {
          dbFinalPrice: totalDbFinalPrice,
          pricingMode: 'wholesale',
          offerId: offerId,
          totalQuantity: totalSelectedVaraintsQuantity,
          logisticsSkuNumModels: logisticsSkuNumModels,
        } : {}),
      };
      console.log('[PDP Pricing] totalVariants:', totalVariants, 'totalVariantsWeight:', totalVariantsWeight, 'dw sent:', calculationData.dw, 'DEFAULT_DW:', PRICE_LOGIC.DEFAULT_DW, 'hasPriceLogs:', hasPriceLogs, 'totalDbFinalPrice:', totalDbFinalPrice);

      // Use API service for pricing calculation (automatically handles auth from localStorage)
      const data = await ProductsApiService.calculatePricingAlgorithm(calculationData, authData.loginHeader, signal);
      */

      // NEW CODE: Single API call to /pricing-algorithms/get-final-price
      // Calculate total actual price (ap) and total weight (dw) for the payload
      // const { totalVariantsBasePrice, totalVariantsWeight, totalVariants } = Object.values(selectedVariants).reduce(
      //   (sum, v) => {
      //     if (v.quantity > 0) {
      //       const originalPrice = parseFloat(v.cp || v.consignPrice || v.ap || v.fenxiaoPriceInfo?.onePiecePrice || v.fenxiaoPriceInfo?.offerPrice || v.price || 0);
      //       let priceToUse = originalPrice;
      //       // Apply quantity-based discounts from priceRanges for wholesale mode
      //       if (priceRanges?.length > 0) {
      //         if (!isMinMaxRange && isWholesale && priceRanges?.length > 0 && totalSelectedVaraintsQuantity > 0) {
      //           const rangePrice = getPriceForQuantity(originalPrice, totalSelectedVaraintsQuantity, priceRanges);
      //           if (rangePrice !== null) {
      //             priceToUse = rangePrice.price;
      //           } else {
      //             priceToUse = priceRanges[0].price;
      //           }
      //         }
      //       }
      //       return {
      //         totalVariants: sum.totalVariants + v.quantity,
      //         totalVariantsBasePrice: sum.totalVariantsBasePrice + priceToUse * (v.quantity || 1),
      //         totalVariantsWeight: sum.totalVariantsWeight + (v.chargeableWeight || v.weight || 0) * (v.quantity || 1),
      //       };
      //     }
      //     return sum;
      //   },
      //   { totalVariantsBasePrice: 0, totalVariantsWeight: 0, totalVariants: 0 }
      // );
      // console.log('totalVariantsBasePrice', totalVariantsBasePrice)
      // console.log('totalVariantsWeight', totalVariantsWeight)
      // console.log('totalVariants', totalVariants);

      // Prepare payload for single API call with offerId and variants array
      const variants = Object.values(selectedVariants)
        .filter(v => v.quantity > 0 && v.skuId)
        .map(v => ({
          skuId: v.skuId,
          selectedQuantity: v.quantity,
          price: v.calculatedFBP,
          cp: Number(v?.cp || v?.consignPrice),
          weight: v.weight
        }));

      const payload = { offerId, variants };

      console.log('[DEBUG] Making API call to getFinalPrice', { payload, timestamp: new Date().toISOString() });

      const response = await ProductsApiService.getFinalPrice(payload);

      if (signal.aborted) return null;

      // const data = await response.json();

      if (response.status?.code === 200 && response.data) {
        const breakdown = response.data;
        console.log('[DEBUG] API response received', { timestamp: new Date().toISOString() });
        setPricingData(breakdown);
        if (!isAutoUpdate) {
          setHasFetchedPricing(true);
        }
        return breakdown;
      }
      return null;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching pricing data:', error);
      }
      return null;
    } finally {
      if (!signal.aborted) {
        if (isAutoUpdate) {
          setAutoUpdating(false);
        } else {
          setLoadingPricing(false);
        }
      }
    }
  };

  // Fetch dropshipping pricing breakdown from APIs (for products with hasPriceLogs)
  const fetchDropshippingPricing = async (isAutoUpdate = false) => {
    const hasSelection = Object.values(selectedVariants).some(v => v.quantity > 0);
    if (isWholesale || !hasPriceLogs || !hasSelection || !totalSelectedVaraintsQuantity) {
      if (!isWholesale && !hasPriceLogs) {
        // For regular dropshipping without price logs, don't clear pricing data
        return null;
      }
      setPricingData(null);
      setAutoUpdating(false);
      return null;
    }

    // Cancel previous request if exists
    if (pricingAbortControllerRef.current) {
      pricingAbortControllerRef.current.abort();
    }

    pricingAbortControllerRef.current = new AbortController();
    const signal = pricingAbortControllerRef.current.signal;

    setAutoUpdating(true);
    setPricingData(null);

    try {
      // Build dynamic logistics SKU models from all selected variants
      const logisticsSkuNumModels = Object.values(selectedVariants)
        .filter(v => v.quantity > 0 && v.skuId)
        .map(v => ({ skuId: v.skuId, quantity: v.quantity }));
      if (!logisticsSkuNumModels.length) {
        setAutoUpdating(false);
        return null;
      }

      // Get offerId from product
      const offerId = product?.offerId || product?.offer_id || product?.sku;
      if (!offerId) {
        setAutoUpdating(false);
        return null;
      }

      // Fetch freight cost
      let shippingCost = 0;
      const freightResult = await ProductsApiService.getLocalShippingCost(parseFloat(offerId), logisticsSkuNumModels, signal);

      if (signal.aborted) return null;

      if (freightResult.success) {
        shippingCost = freightResult.shippingCost;
      }

      // Calculate total DB final price for dropshipping (use finalPrice)
      let totalDbFinalPrice = 0;
      if (dbVariantPrices) {
        Object.values(selectedVariants).forEach(v => {
          if (v.quantity > 0 && dbVariantPrices[v.skuId]) {
            const dbVariant = dbVariantPrices[v.skuId];
            // Use finalPrice for dropshipping mode
            const dbPrice = parseFloat(dbVariant.finalPrice || 0);
            totalDbFinalPrice += dbPrice * v.quantity;
          }
        });
      }

      if (totalDbFinalPrice <= 0) {
        setAutoUpdating(false);
        return null;
      }

      // Calculate total weight and volume
      const { totalVariantsWeight, totalVariantsVolume, totalVariants } = Object.values(selectedVariants).reduce(
        (sum, v) => {
          if (v.quantity > 0) {
            return {
              totalVariants: sum.totalVariants + v.quantity,
              totalVariantsWeight: sum.totalVariantsWeight + (v.chargeableWeight || v.weight || 0) * (v.quantity || 1),
              totalVariantsVolume: sum.totalVariantsVolume + (v.height && v.width && v.length ? v.height * v.width * v.length * (v.quantity || 1) : 0),
            };
          }
          return sum;
        },
        { totalVariantsWeight: 0, totalVariantsVolume: 0, totalVariants: 0 }
      );

      const calculationData = {
        ap: 0, // Not used for reverse calculation
        ls: shippingCost,
        hsn: null,
        hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
        dw: totalVariantsWeight > 0 ? totalVariantsWeight : (PRICE_LOGIC.DEFAULT_DW || 0.5) * totalVariants,
        vw: totalVariantsVolume / PRICE_LOGIC.VOLUME_FACTOR,
        categoryId: product?.categoryId || '',
        topCategoryId: product?.topCategoryId || '',
        secondCategoryId: product?.secondCategoryId || '',
        thirdCategoryId: product?.thirdCategoryId || '',
        productShippingInfo: product?.productShippingInfo || product?.productShippingInfo || {},
        dbFinalPrice: totalDbFinalPrice,
        pricingMode: 'dropshipping',
        offerId: offerId,
        totalQuantity: totalSelectedVaraintsQuantity,
        logisticsSkuNumModels: logisticsSkuNumModels,
      };

      console.log('[PDP Dropshipping Pricing] hasPriceLogs:', hasPriceLogs, 'totalDbFinalPrice:', totalDbFinalPrice);

      const data = await ProductsApiService.calculatePricingAlgorithm(calculationData, authData.loginHeader, signal);

      if (signal.aborted) return null;

      if (data.status.code === 200 && data.data?.breakdown) {
        const breakdown = data.data.breakdown;
        setPricingData(breakdown);
        return breakdown;
      }
      return null;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching dropshipping pricing data:', error);
      }
      return null;
    } finally {
      if (!signal.aborted) {
        setAutoUpdating(false);
      }
    }
  };

  // Handle quantity change and update freight cost
  const handleQuantityChange = async (quantity, skuId = null) => {
    if (product && Object.keys(product).length > 0) {
      await fetchLocalShippingCost(product.offer || product.sku, [{ skuId: skuId, quantity: quantity }], message);
    }
  };

  // Centralized pricing calculation function
  const calculatePricing = async (actualPrice, productCategoryId = null, variant = {}) => {
    try {
      // Check authentication
      // if (!isLoggedIn) {
      //   message.showToast('User not authenticated or not authorized for pricing', ToastTypes.ERROR);
      //   return null;
      // }

      const calculationData = {
        ap: actualPrice,
        ls: 0, // for now there is no LS required here for first time Ali's Product's calculations. (we'll handle it later)
        hsn: false,
        hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
        dw: variant.weight || PRICE_LOGIC.DEFAULT_DW,
        vw: variant.height && variant.width && variant.length ? variant.height * variant.width * variant.length : 0,
        ...(productCategoryId ? { categoryId: productCategoryId } : {}), // only parse categoryId if it is provided
        sku: String(variant.sku || variant.skuId || product?.offerId || product?.sku || ''),
      };

      const data = await ProductsApiService.calculatePricingAlgorithm(calculationData);
      if (data.status.code === 200 && data.data.breakdown.CP) {
        const calculatedPrice = data.data.breakdown.CP;
        const finalPrice = data.data.breakdown.FBP;
        const breakdown = data.data.breakdown;
        return {
          costPrice: calculatedPrice,
          finalPrice: finalPrice,
          breakdown,
        };
      } else {
        throw new Error('Invalid response from pricing API');
      }
    } catch (error) {
      message.showToast('Pricing calculation error', ToastTypes.ERROR);
      return null;
    }
  };

  const handleProduct = async id => {
    setPdLoading(true);
    setProductError(null); // Clear any previous errors
    loader.showOverlayLoader('Loading product details...', 'large');

    try {
      const res = await ProductsApiService.getProductById(id);
      loader.hideLoader();
      setPdLoading(false);

      if (res.status.code === 200) {
        const result = res.data;
        // This Logic is for translate Mandarin into English.
        // Fields for translate.
        const { companyName, productSaleInfo, variations: productSkuInfos } = result;
        const toBeTranslatedData = { companyName, productSaleInfo, productSkuInfos };
        // COMMENTED OUT: Bulk API call on PDP
        // const { saleInfoText, sellerInfoText, productVariantsLabelGroupText, productVariantsValueGroupText } = await TranslationService.translateProductTexts(toBeTranslatedData);
        // Use empty objects as fallback
        const saleInfoText = {};
        const sellerInfoText = {};
        const productVariantsLabelGroupText = {};
        const productVariantsValueGroupText = {};

        const mapped = {
          ...res.data,
          month_sold: result.monthSoldOut,
          supplierLogo: appendSizeToImageUrl(result.image, `400x400`),
          saleInfo: saleInfoText,
          soldOut: result.monthSoldOut || 0,
          sellerDataInfo: result.sellerDataInfo || {}, // complete rating object
          mainVideo: result.mainVideo || null, // Main video from DB
        };

        setMOQ(mapped.moq);

        // Check if dropshipping is available for this product
        const isProductDropshippingAvailable = isDropshippingAvailableFromTags(result.tagInfoList || []);

        // Default PDP tab: One-Piece only when (MOQ === 1) and 1-piece is enabled via tags
        const isOnePieceAvailable = Number(mapped.moq) === 1 && isProductDropshippingAvailable;
        const desiredPricingMode = isOnePieceAvailable
          ? SellingModes.DropShipping
          : SellingModes.Wholesale;
        setPricingMode(desiredPricingMode);

        // Set customMOQ: Only convert MOQ 1 to 2 if dropshipping is available
        if (mapped.moq && mapped.moq > 0) {
          if (mapped.moq === 1 && desiredPricingMode === SellingModes.Wholesale && isProductDropshippingAvailable) {
            setCustomMOQ(2);
          } else {
            setCustomMOQ(mapped.moq);
          }
        }

        setProduct(mapped);
        setIsDbProduct(true);
        setProduct(res.data);
        const priceRangeList = res.data.priceRanges;
        setPriceRanges(priceRangeList);

        const variantsDetails = {};
        res.data.variations.forEach(variant => {
          // Apply chargeableWeight calculation for DB products (same as Alibaba products)
          variantsDetails[variant.skuId] = calculateVariantChargeableWeight(variant, res.data);
        });

        setVariantDetails(variantsDetails);
        setVariantPrices(variantsDetails);

        // Initialize preferred image - video will be handled by ProductDetailLeft component
        // Priority: mainVideo (handled in component) > first imgUrl
        if (res.data.mainVideo) {
          // Video exists - let ProductDetailLeft handle it, set image as fallback
          setPreferredImage(res.data.image || res.data.imgUrl?.[0] || '');
        } else if (res.data.image) {
          setPreferredImage(res.data.image);
        } else if (res.data.imgUrl && res.data.imgUrl.length > 0) {
          setPreferredImage(res.data.imgUrl[0]);
        }
      } else {
        message.showToast(res.error.message, ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      loader.hideLoader();
      setPdLoading(false);
    }
  };

  const handleAlibabaProduct = async offer => {
    try {
      setPdLoading(true);
      setProductError(null); // Clear any previous errors
      loader.showOverlayLoader('Loading product details...', 'large');

      // OPTIMIZATION: Check Solr cache first for instant loading
      // console.log(`🔍 Checking Solr cache for product: ${offer}`);
      // const cachedProduct = await SolrService.getProductDetails(offer);

      // if (cachedProduct && cachedProduct.mapped && cachedProduct.variantsDetails) {
      //   console.log(`✅ Found cached product details for: ${offer}`);

      //   // Restore from cache - instant loading!
      //   const restoredMapped = {
      //     ...cachedProduct.mapped,
      //     variations: Object.values(cachedProduct.variantsDetails),
      //   };

      //   setProduct(restoredMapped);
      //   setVariantDetails(cachedProduct.variantsDetails);
      //   setActualVariants(cachedProduct.variantsDetails); // Use same for actual
      //   setMarkupPercentValue(cachedProduct.markupPercentage || 0);

      //   if (cachedProduct.priceRanges && cachedProduct.priceRanges.length > 0) {
      //     setPriceRanges(cachedProduct.priceRanges);
      //   }

      //   if (restoredMapped.imgUrl && restoredMapped.imgUrl.length > 0) {
      //     setPreferredImage(restoredMapped.imgUrl[0]);
      //   }

      //   setPdLoading(false);
      //   loader.hideLoader();
      //   return;
      // }

      console.log(`📡 Cache miss - fetching from API for: ${offer}`);
      const startTime = Date.now();

      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const resp = await fetch(baseUrl + '/product-detail-by-offerid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
        body: JSON.stringify({ offerId: offer, country: 'en' }),
      });
      const safe = await resp.json();

      // Check if the API returned an error response
      if (safe.result && safe.result.success === false) {
        const errorMessage = safe.result.message || 'The product does not exist.';
        setProductError(errorMessage);
        setPdLoading(false);
        loader.hideLoader();
        return;
      }

      // Clear any previous errors
      setProductError(null);
      const result = safe?.result?.result || safe?.result || (safe?.offerId || safe?.productSkuInfos ? safe : {}) || {};
      setAliDetail(result);

      // Add DW(Dead weight) n VW (Volumetric weight) info into variantsToCalculate.
      // type variant = {
      // height: number,
      // length: number,
      // skuId: number,
      // specId: string,
      // weight: number,
      // width: number,
      // }

      const shippingInfo = result.productShippingInfo ?? {};

      // OPTIMIZATION: Run translation in parallel with other operations
      let saleInfoText = Object.create(null);
      let sellerInfoText = Object.create(null);
      let productInfoText = Object.create(null);
      let productVaraintsLabelGroupText = Object.create(null);
      let productVaraintsValueGroupText = Object.create(null);

      const isTitleTranslatable = result.subjectTrans === result.subject || !result.subjectTrans;
      if (isTitleTranslatable) productInfoText.title = result.subject;

      const productTitle = result.title;
      const productUnit = result.productSaleInfo?.unitInfo?.unit;
      const transProductUnit = result.productSaleInfo?.unitInfo?.transUnit || '';
      const isUnitTranslatable = productUnit && !((transProductUnit || '').includes('piece') || (transProductUnit || '').includes('unit'));
      if (isUnitTranslatable) saleInfoText.unit = productUnit;
      if (result.companyName) sellerInfoText.companyName = result.companyName;
      if (result.productSkuInfos?.length > 0) {
        // Add grouped labels of product's all variants n first value of attr group.
        if (result.productSkuInfos[0].skuAttributes?.length > 0) {
          result.productSkuInfos[0].skuAttributes.forEach(attr => {
            if (attr.attributeName && !productVaraintsLabelGroupText[attr.attributeName]) productVaraintsLabelGroupText[attr.attributeName] = attr.attributeName;
            if (attr.value && !productVaraintsValueGroupText[attr.value]) productVaraintsValueGroupText[attr.value] = attr.value;
          });
        }
        // Add all varaint's grouped label's specific values after first one.
        for (let i = 1; i < result.productSkuInfos.length; i++) {
          const productSkuInfo = result.productSkuInfos[i];
          productSkuInfo.skuAttributes?.forEach(attr => {
            if (attr.value && !productVaraintsValueGroupText[attr.value]) productVaraintsValueGroupText[attr.value] = attr.value;
          });
        }
      }

      // Build mapped product early for faster UI update
      // Note: sp and consign_price will be updated after priceRanges are calculated
      // Initially set to 0 to avoid using raw RMB price
      // Consign price: prefer productSaleInfo.consignPrice when present (e.g. for no-variant products), else first price range
      const consignFromSaleInfo = result.productSaleInfo?.consignPrice ?? result.productSaleInfo?.consignprice;
      const consignPrice = consignFromSaleInfo != null ? consignFromSaleInfo : (result.productSaleInfo?.priceRangeList?.[0]?.price);
      const imageList = Array.isArray(result.productImage?.images)
        ? result.productImage.images
        : Array.isArray(result.images)
          ? result.images
          : [];
      const mapped = {
        sku: String(offer),
        offerId: String(offer), // Add offerId for dropshipping price calculation
        title: result.subjectTrans || result.subject || 'Alibaba Product',
        sp: consignPrice ?? result.productSaleInfo?.priceRangeList?.[0]?.price,
        consign_price: consignPrice ?? result.productSaleInfo?.priceRangeList?.[0]?.price,
        image: appendSizeToImageUrl(imageList[0] || result.whiteImage || result.whiteimage, `400x400`),
        imgUrl: imageList,
        mainVideo: result.mainVideo || null, // Main product video from Alibaba API
        description: result.description && !result.description.includes('offer_template')
          ? result.description
          : (result.desc || ''), // Filter out placeholder descriptions
        img_description: result.description || result.desc || '', // Keep original for reference
        location: 'global', // Set to 'global' for Alibaba products
        handling_days: result.productShippingInfo?.handlingTime || 15,
        month_sold: parseInt(result.soldOut || result.monthSold || 0), // Add month_sold field
        moq: result.minOrderQuantity || 0,
        // Batch number from Alibaba data (for batch-based quantity increments)
        batchNumber: result.batchNumber || null,
        categoryId: result.categoryId || null,
        companyName: result.companyName, // Will be updated after translation
        supplierLogo: appendSizeToImageUrl(imageList[0] || result.whiteImage || result.whiteimage, `400x400`),
        saleInfo: saleInfoText,
        sellerDataInfo: result.sellerDataInfo || {}, // complete rating object
        soldOut: result.soldOut || 0,
        productAttribute: result.productAttribute || [],
        productSaleInfo: result.productSaleInfo || {}, // Include productSaleInfo for stock checking
        tagInfoList: result.tagInfoList || [], // Include tagInfoList for checking isOnePsale
        topCategoryId: result.topCategoryId || '',
        secondCategoryId: result.secondCategoryId || '',
        thirdCategoryId: result.thirdCategoryId || '',
        productShippingInfo: shippingInfo || {},
      };

      const mandarinTextData = { saleInfoText, sellerInfoText, productVaraintsLabelGroupText, productVaraintsValueGroupText, productInfoText };
      // COMMENTED OUT: Bulk API call on PDP
      const translationResult = await ProductsApiService.translateMandarinToEnglish(mandarinTextData, 'en');

      // Apply translation results
      // COMMENTED OUT: Bulk API call on PDP
      if (translationResult.status?.code === 200 && translationResult.data) {
        const {
          saleInfoText: translatedSaleInfoText,
          sellerInfoText: translatedSellerInfoText,
          productVaraintsLabelGroupText: translatedProductVaraintsLabelGroupText,
          productVaraintsValueGroupText: translatedProductVaraintsValueGroupText,
          productInfoText: translatedProductInfoText,
        } = translationResult.data;

        saleInfoText = isUnitTranslatable ? translatedSaleInfoText : { unit: transProductUnit };
        sellerInfoText = translatedSellerInfoText || sellerInfoText;
        productVaraintsLabelGroupText = translatedProductVaraintsLabelGroupText || productVaraintsLabelGroupText;
        productVaraintsValueGroupText = translatedProductVaraintsValueGroupText || productVaraintsValueGroupText;
        productInfoText = translatedProductInfoText || productInfoText;

        // Update mapped with translated values
        mapped.companyName = sellerInfoText.companyName ?? result.companyName;
        mapped.saleInfo = saleInfoText;
        if (isTitleTranslatable) mapped.title = productInfoText.title;
      }


      // Set product early for faster perceived loading
      setMOQ(mapped.moq);
      setProduct(mapped);

      // Initialize preferred image - video will be handled by ProductDetailLeft component
      // Priority: mainVideo (handled in component) > first imgUrl
      if (mapped.imgUrl && mapped.imgUrl.length > 0) {
        setPreferredImage(mapped.imgUrl[0]);
      }

      const variantsDetails = {};
      const actualVariants = {};
      let minVariantWeight = Infinity;
      let minVariantVolume = Infinity;
      // Map variations using centralized pricing service
      const variationsResult = await CentralizePriceCalculation.mapAlibabaVariations(result.productSkuInfos, shippingInfo, mapped);
      mapped.variations = variationsResult.variations;

      Object.assign(variantsDetails, variationsResult.variantsDetails);
      Object.assign(actualVariants, variationsResult.actualVariants);
      minVariantWeight = variationsResult.minVariantWeight;
      minVariantVolume = variationsResult.minVariantVolume;

      setProduct(mapped);

      // Store variant details once variant details are fetched from Alibaba.
      setVariantDetails(variantsDetails);
      setActualVariants(actualVariants);

      // Pre-populate variantPrices with calculated prices from variantsDetails
      // This avoids on-demand API calls for dropshipping prices
      const preCalculatedPrices = {};
      Object.entries(variantsDetails).forEach(([skuId, variant]) => {
        if (variant.finalPrice) {
          preCalculatedPrices[skuId] = {
            finalPrice: variant.finalPrice,
            breakdown: variant.breakdown,
            localShipping: variant.localShipping,
            ls: variant.ls,
          };
        }
      });

      // For products without variants, calculate price and add to variantPrices
      if (Object.keys(variantsDetails).length === 0 && result?.productSaleInfo?.priceRangeList?.length > 0) {
        const noVariantKey = mapped.sku || mapped.offerId || 'no-variant';
        const firstPriceRange = result.productSaleInfo.priceRangeList[0];
        const rawRMBPrice = parseFloat(firstPriceRange.price) || 0;

        if (rawRMBPrice > 0) {
          try {
            // Calculate dropshipping price for product without variants
            const calc = await ProductsApiService.calculatePricingAlgorithm({
              ap: rawRMBPrice,
              ls: 0, // Will use algorithm's local_shipping_rmb
              hsn: null,
              hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
              dw: minVariantWeight || PRICE_LOGIC.DEFAULT_DW,
              vw: (minVariantVolume || 0) / PRICE_LOGIC.VOLUME_FACTOR,
              categoryId: mapped.categoryId || '',
              sku: String(mapped.offerId || mapped.sku || ''),
              topCategoryId: mapped.topCategoryId || '',
              secondCategoryId: mapped.secondCategoryId || '',
              thirdCategoryId: mapped.thirdCategoryId || '',
              productShippingInfo: shippingInfo || {},
            });

            const FBP = calc?.data?.breakdown?.FBP;
            const LS = calc?.data?.breakdown?.LS;

            if (FBP) {
              preCalculatedPrices[noVariantKey] = {
                finalPrice: FBP,
                breakdown: calc?.data?.breakdown,
                localShipping: LS || 0,
                ls: 0,
              };
            }
          } catch (error) {
            console.error('Failed to calculate price for product without variants:', error);
          }
        }
      }

      setVariantPrices(preCalculatedPrices);

      // Update preferred image after full product load (priority to first imgUrl)
      if (mapped.imgUrl && mapped.imgUrl.length > 0) {
        setPreferredImage(mapped.imgUrl[0]);
      }

      // OPTIMIZATION: Calculate price ranges in parallel batches
      let finalPriceRanges = [];
      if (result?.productSaleInfo?.priceRangeList) {
        // const baseRanges = result.productSaleInfo.priceRangeList || [];
        // const offerIdNum = parseFloat(offer);

        // // Process price ranges in parallel (max 3 concurrent)
        // const batchSize = 3;
        // for (let i = 0; i < baseRanges.length; i += batchSize) {
        //   const batch = baseRanges.slice(i, i + batchSize);
        //   const batchResults = await Promise.all(
        //     batch.map(async (range, idx) => {
        //       try {
        //         const actualIdx = i + idx;
        //         if (!isMinMaxRange && range.startQuantity === 1) {
        //           range.startQuantity = WholesaleInitialQuantity;
        //         }
        //         if (actualIdx === 0) setCustomMOQ(range.startQuantity);

        //         const lsRes = await ProductsApiService.getLocalShippingCost(offerIdNum, [{ skuId: offerIdNum, quantity: range.startQuantity }]);
        //         const ls = lsRes.success ? lsRes.shippingCost : 0;
        //         const finalLS = ls / range.startQuantity;

        //         const calc = await ProductsApiService.calculatePricingAlgorithm({
        //           ap: parseFloat(range.price),
        //           ls: finalLS,
        //           hsn: null,
        //           hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
        //           dw: maxVariantWeight || PRICE_LOGIC.DEFAULT_DW,
        //           vw: maxVariantVolume / PRICE_LOGIC.VOLUME_FACTOR || 0,
        //           categoryId: mapped.categoryId || '',
        //           sku: String(mapped.offerId || mapped.sku || ''),
        //         });
        //         const FBP = calc?.data?.breakdown?.FBP;
        //         return { ...range, finalPrice: FBP };
        //       } catch {
        //         message.showToast(`Failed to Fetch One of Price Range`, ToastTypes.ERROR);
        //         return null;
        //       }
        //     })
        //   );
        //   finalPriceRanges.push(...batchResults.filter(r => r && r.finalPrice));
        // }
        // setPriceRanges(finalPriceRanges);



        const offerIdNum = parseFloat(offer);
        const baseRanges = result.productSaleInfo.priceRangeList || [];
        const categoryId = mapped.categoryId || '';
        const isMappedDropshippingAvailable = isDropshippingAvailableFromTags(mapped.tagInfoList || []);
        // Default PDP tab: One-Piece only when (MOQ === 1) and 1-piece is enabled via tags
        const isOnePieceAvailable = Number(mapped.moq) === 1 && isMappedDropshippingAvailable;
        const desiredPricingMode = isOnePieceAvailable
          ? SellingModes.DropShipping
          : SellingModes.Wholesale;
        setPricingMode(desiredPricingMode);
        // Use MOQ from API if available
        // Special case: If MOQ is 1 and in wholesale mode, convert to 2
        // Otherwise, use WholesaleInitialQuantity only if startQuantity is 1
        if (mapped.moq && mapped.moq > 0) {
          // If MOQ is 1 and in wholesale mode, set to 2, otherwise use actual MOQ
          if (mapped.moq === 1 && desiredPricingMode === SellingModes.Wholesale && isMappedDropshippingAvailable) {
            setCustomMOQ(2);
          } else {
            setCustomMOQ(mapped.moq);
          }
        } else {
          // set Custom MOQ only if min~max is not present n starQuantity is 1 for Wholesale. (pay attention on this line, crucial).
          baseRanges.forEach(range => (!isMinMaxRange && range.startQuantity === 1 && WholesaleInitialQuantity ? setCustomMOQ(WholesaleInitialQuantity) : null));
        }
        if (minVariantVolume === Infinity) minVariantVolume = 0;
        if (minVariantWeight === Infinity) minVariantWeight = 0;
        const priceFBP_Ranges = await CentralizePriceCalculation.getCalculatedPriceRangeList(
          offerIdNum,
          baseRanges,
          categoryId,
          WholesaleInitialQuantity,
          minVariantVolume,
          minVariantWeight,
          isMappedDropshippingAvailable,
          mapped
        );
        setPriceRanges(priceFBP_Ranges);

        // Update product.sp and consign_price with converted prices from first price range
        if (priceFBP_Ranges && priceFBP_Ranges.length > 0 && priceFBP_Ranges[0].finalPrice) {
          mapped.sp = priceFBP_Ranges[0].finalPrice;
          mapped.consign_price = priceFBP_Ranges[0].finalPrice;
          setProduct({ ...mapped }); // Update product with converted prices
        }
        // Apply markup to price ranges for UI display while FBP loads
        // const priceRangeList = applyMarkupToPriceRanges(priceFBP_Ranges, pricingAlgoData.formula?.markup_percent);
        // setPriceRanges(priceRangeList);
      }

      // COMMENTED OUT: Solr caching is disabled - now only using DB and API
      // OPTIMIZATION: Cache product details in Solr for future fast loading
      const loadTime = Date.now() - startTime;
      console.log(`⏱️ Product loaded from API in ${loadTime}ms`);

      // COMMENTED OUT: Cache in Solr
      // SolrService.storeProductDetails(offer, {
      //   mapped,
      //   variantsDetails,
      //   priceRanges: finalPriceRanges,
      // }).then(success => {
      //   if (success) {
      //     console.log(`✅ Product ${offer} successfully cached to Solr`);
      //   } else {
      //     console.error(`❌ Failed to cache product ${offer} to Solr`);
      //   }
      // }).catch(err => {
      //   console.error(`❌ Solr caching error for product ${offer}:`, err);
      // });

    } catch (e) {
      message.showToast(`Failed to load product details: ${e.message}`, ToastTypes.ERROR);
      loader.hideLoader();
    } finally {
      setPdLoading(false);
      loader.hideLoader();
    }
  };

  // Handler for products found in DB by offerId (similar to handleProduct but uses offerId lookup)
  const handleDbProductByOfferId = async (offerId) => {
    console.log('handleDbProductByOfferId called with offerId:', offerId);
    setPdLoading(true);
    setProductError(null);
    loader.showOverlayLoader('Loading product details...', 'large');

    try {
      const res = await ProductsApiService.getProductByOfferId(offerId);
      console.log('res', res)

      if (res.status.code === 200 && res.data) {
        const result = res.data?.data && typeof res.data.data === 'object' && res.data.offerId == null ? res.data.data : res.data;
        loader.hideLoader();
        setPdLoading(false);

        // Translate Mandarin into English
        const { companyName, productSaleInfo, variations: productSkuInfos } = result;
        const toBeTranslatedData = { companyName, productSaleInfo, productSkuInfos };
        // COMMENTED OUT: Bulk API call on PDP
        // const { saleInfoText, sellerInfoText } = await TranslationService.translateProductTexts(toBeTranslatedData);
        // Use empty objects as fallback
        const saleInfoText = {};
        const sellerInfoText = {};

        const mapped = {
          ...result,
          month_sold: result.monthSoldOut,
          supplierLogo: appendSizeToImageUrl(result.image, `400x400`),
          saleInfo: saleInfoText,
          soldOut: result.monthSoldOut || 0,
          sellerDataInfo: result.sellerDataInfo || {},
          mainVideo: result.mainVideo || null,
        };

        setMOQ(mapped.moq);

        // Check if dropshipping is available for this product
        const isProductDropshippingAvailable = isDropshippingAvailableFromTags(result.tagInfoList || []);

        // Default PDP tab: One-Piece only when (MOQ === 1) and 1-piece is enabled via tags
        const isOnePieceAvailable = Number(mapped.moq) === 1 && isProductDropshippingAvailable;
        const desiredPricingMode = isOnePieceAvailable
          ? SellingModes.DropShipping
          : SellingModes.Wholesale;
        setPricingMode(desiredPricingMode);

        // Set customMOQ: Only convert MOQ 1 to 2 if dropshipping is available
        if (mapped.moq && mapped.moq > 0) {
          if (mapped.moq === 1 && desiredPricingMode === SellingModes.Wholesale && isProductDropshippingAvailable) {
            setCustomMOQ(2);
          } else {
            setCustomMOQ(mapped.moq);
          }
        }

        setProduct(mapped);
        setIsDbProduct(true);

        const priceRangeList = result.priceRanges;
        setPriceRanges(priceRangeList || []);

        const variationRows = Array.isArray(result.variations)
          ? result.variations
          : Array.isArray(result.productSkuInfos)
            ? result.productSkuInfos.map(v => ({
              ...v,
              skuId: v?.skuId || v?.specId || null,
            }))
            : [];

        const variantsDetails = {};
        variationRows.forEach(variant => {
          if (variant?.skuId == null) return;
          variantsDetails[variant.skuId] = calculateVariantChargeableWeight(variant, result);
        });

        setVariantDetails(variantsDetails);
        setVariantPrices(variantsDetails);

        // Initialize preferred image
        if (result.mainVideo) {
          setPreferredImage(result.image || result.imgUrl?.[0] || '');
        } else if (result.image) {
          setPreferredImage(result.image);
        } else if (result.imgUrl && result.imgUrl.length > 0) {
          setPreferredImage(result.imgUrl[0]);
        }

        return true; // Found in DB
      }

      loader.hideLoader();
      setPdLoading(false);
      return false; // Not found in DB
    } catch (err) {
      loader.hideLoader();
      setPdLoading(false);
      console.log(`DB lookup failed for offerId ${offerId}, falling back to API`);
      return false; // Error, fall back to API
    }
  };

  useEffect(() => {
    // Reset product state when URL parameters change
    setProduct({});
    setVariantDetails({});
    setPriceRanges([]);
    setSelectedVariants({});
    setVariantPrices({});
    setPreferredImage('');
    setHasPriceLogs(false);
    setDbVariantPrices(null);

    const fetchProduct = async () => {
      if (ali === '1' && offer) {
        // Check if price logs exist for this offerId (for DB variant price based calculations)
        try {
          const priceLogsRes = await ProductsApiService.checkPriceLogsForOffer(offer);
          if (priceLogsRes?.status?.code === 200 && priceLogsRes?.data) {
            setHasPriceLogs(priceLogsRes.data.hasPriceLogs || false);
            setDbVariantPrices(priceLogsRes.data.variantPrices || null);
          }
        } catch (err) {
          console.log('Failed to check price logs:', err);
        }

        // First check if product exists in DB by offerId
        const foundInDb = await handleDbProductByOfferId(offer);
        if (!foundInDb) {
          // Not found in DB, fall back to Alibaba API
          handleAlibabaProduct(offer);
        }
      } else if (id) {
        // Product detail mode: id=<productId> (db product)
        handleProduct(id);
      }
    };

    fetchProduct();
  }, [ali, offer, id, searchParams]); // Watch for changes in URL parameters

  // Removed: Auto-set pricing mode based on MOQ. All products now default to Wholesale.
  // useEffect(() => {
  //   const moq = product.moq ?? product.minOrderQuantity;
  //   if (moq === 1) {
  //     setPricingMode(SellingModes.DropShipping);
  //   } else if (moq > 1) {
  //     setPricingMode(SellingModes.Wholesale);
  //   }
  // }, [product.minOrderQuantity, product.moq]);

  // Update customMOQ when MOQ is available from API
  // Special case: If MOQ is 1 and in wholesale mode with dropshipping available, set to 2
  // Otherwise, use the actual MOQ from API
  useEffect(() => {
    const actualMOQ = MOQ || product.moq || product.minOrderQuantity || 0;

    if (actualMOQ > 0) {
      // If MOQ is 1 and in wholesale mode with dropshipping available, convert to 2
      if (actualMOQ === 1 && isWholesale && isDropshippingAvailable) {
        setCustomMOQ(2);
      } else {
        // Use actual MOQ for all other cases
        setCustomMOQ(actualMOQ);
      }
    }
  }, [MOQ, product.moq, product.minOrderQuantity, isWholesale, pricingMode, isDropshippingAvailable]);

  const handleQuantityChangeForVariant = (skuId, quantity, variant) => {
    setSelectedVariants(prev => {
      const updated = { ...prev };
      if (quantity > 0) {
        // Store variant with quantity
        // For dropshipping: merge calculated price from variantPrices if available
        // But preserve variant's own price fields as they contain the specific variant's price
        let variantWithPrice = { ...variant, quantity };

        // If variantPrices has calculated price for this variant, merge it
        // This ensures we use the calculated dropshipping price if available
        if (variantPrices[skuId] && variantPrices[skuId].finalPrice) {
          variantWithPrice = {
            ...variant,
            ...variantPrices[skuId],
            quantity,
            // Preserve variant's original price fields as fallback
            finalPrice: variantPrices[skuId].calculatedFBP || variant.finalPrice,
          };
        }

        updated[skuId] = variantWithPrice;
      } else {
        delete updated[skuId];
      }
      return updated;
    });
  };

  // Add to cart function
  const addToCart = async (discountAmountFromOrderSummary) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    console.log('Calling addtoCart')

    const selectedVars = Object.values(selectedVariants).filter(v => v.quantity > 0 && v.skuId);
    console.log('selectedVars', selectedVars)
    if (selectedVars.length === 0) {
      message.showToast('Please select at least one variant with quantity', ToastTypes.WARNING);
      return;
    }
    if (isWholesale && wholesaleMOQToEnforce > 0 && totalSelectedVaraintsQuantity < wholesaleMOQToEnforce) {
      message.showToast(`Minimum wholesale MOQ is ${wholesaleMOQToEnforce}. Please add at least ${wholesaleMOQToEnforce} units.`, ToastTypes.WARNING);
      return;
    }

    const toastId = message.showToast('Adding to cart...', ToastTypes.LOADING);

    try {
      // Only send skuId and quantity for each variant
      const variants = selectedVars.map(variant => ({ skuId: variant.skuId, quantity: variant.quantity }));

      const productPayload = {
        offerId: product.sku,
        variants,
        pricingMode,
        isMinMaxRange,
      };
      console.log('Adding to cart with payload:', productPayload);

      // Include breakdown if available (from wholesale pricing calculation)
      if (isWholesale && pricingData) {
        let discountAmount = discountAmountFromOrderSummary;
        let grossTotalAmount = 0;
        if (typeof pricingData.grossTotalAmount === 'number') {
          grossTotalAmount = pricingData.grossTotalAmount;
        }
        if (!grossTotalAmount) {
          grossTotalAmount = Object.values(selectedVariants).reduce((sum, v) => {
            if (v.quantity > 0) {
              let price = v.calculatedFBP || v.finalPrice || v.ap || v.cp || v.consignPrice || (v.fenxiaoPriceInfo && v.fenxiaoPriceInfo.offerPrice) || 0;
              return sum + price * v.quantity;
            }
            return sum;
          }, 0);
        }
        productPayload.breakdown = {
          ...pricingData,
          discountAmount,
          grossTotalAmount
        };
      }

      await CartApiService.AddUpdateCart(JSON.stringify(productPayload));

      message.showToast('Products added to cart successfully!', ToastTypes.SUCCESS, { toastId });
      dispatch(fetchCartItems());

      // Track add to cart event
      if (product && (product.offerId || product.sku || product._id)) {
        const productId = product.offerId || product.sku || product._id;
        if (productId) {
          SearchLogService.trackAddToCart(String(productId), variants.reduce((sum, v) => sum + v.quantity, 0))
            .then(response => { })
            .catch(err => { });
        }
      }
    } catch (error) {
      logger.error('Error adding to cart', { error });
      message.showToast(error.message || 'Error adding products to cart', ToastTypes.ERROR, { toastId });
    }
  };

  // Buy now function
  const buyNow = async () => {
    if (!isLoggedIn) {
      message.showToast('To buy now please login', ToastTypes.WARNING);
      return;
    }

    const selectedVars = Object.values(selectedVariants).filter(v => v.quantity > 0);
    if (selectedVars.length === 0) {
      message.showToast('Please select at least one variant with quantity', ToastTypes.WARNING);
      return;
    }

    if (isWholesale && wholesaleMOQToEnforce > 0 && totalSelectedVaraintsQuantity < wholesaleMOQToEnforce) {
      message.showToast(`Minimum wholesale MOQ is ${wholesaleMOQToEnforce}. Please add at least ${wholesaleMOQToEnforce} units.`, ToastTypes.WARNING);
      return;
    }

    await addToCart();
    navigate(`/checkout/cart?product=${product._id}`);
  };

  // Generate dynamic SEO data for the product
  const productSEO =
    product && Object.keys(product).length > 0
      ? generateProductSEO({
        name: product.name || product.title,
        description: product.desc || product.description,
        price: product.actualPrice || product.price,
        currency: 'INR',
        images: product.imgUrl || [],
        inStock: true,
        brand: product.brand || 'Importerr',
        category: product.category || product.categoryName,
      })
      : null;

  return (
    <div>
      {productSEO && <SEO {...productSEO} />}
      <BreadCrumbs product={product} />
      {pdLoading && (
        <div className="fixed top-[180px] left-[50%] transform translate-x-[-50%] bg-rgba(255,255,255,0.95) flex items-center gap-[12px] z-[1000] rounded-[12px] p-[12px] min-w-[260px] shadow-[0_4px_20px_rgba(0,0,0,0.15)] border-[1px] border-[#e5e7eb]">
          <div className="w-[22px] h-[22px] border-[3px] border-[#e5e7eb] border-t-[#3DD0E5] rounded-full animate-spin" />
          <div className="flex flex-col">
            <strong className="m-0 text-[#004C81] text-[14px]">Loading products</strong>
            <span className="m-0 text-[#6b7280] text-[12px]">Searching… please wait</span>
          </div>
        </div>
      )}
      <div className="product_detail_box  ">
        <div className="pd_lg_screen">
          <ProductDetailLeft product={product} preferredImage={preferredImage} setPreferredImage={setPreferredImage} isLoading={pdLoading} isOpenAskInquiry={isOpenAskInquiry} />
        </div>

        <ProductDetailRight
          loginHeader={authData.loginHeader}
          product={product}
          priceRanges={priceRanges}
          setPreferredImage={setPreferredImage}
          onQuantityChange={handleQuantityChange}
          searchKeyword={searchParams.get('search') || ''}
          variantDetails={variantDetails}
          actualVariants={actualVariants}
          isLoading={pdLoading}
          selectedVariants={selectedVariants}
          setSelectedVariants={setSelectedVariants}
          pricingMode={pricingMode}
          setPricingMode={setPricingMode}
          onQuantityChangeForVariant={handleQuantityChangeForVariant}
          variantPrices={variantPrices}
          WholesaleInitialQuantity={WholesaleInitialQuantity}
          isWholesale={isWholesale}
          onVariantVisible={calculateVariantPrice}
          sortedAttributeGroups={sortedAttributeGroups}
          maxVariantPrice={maxVariantPrice}
          minVariantPrice={minVariantPrice}
          maxWholesalePrice={maxWholesalePrice}
          minWholesalePrice={minWholesalePrice}
          groupedVariants={groupedVariants}
          setTotalWholesalePrice={setTotalWholesalePrice}
          isMinMaxRange={isMinMaxRange}
          customMOQ={customMOQ}
          MOQ={MOQ}
          productError={productError}
          isLoggedIn={isLoggedIn}
          askForPricingQuotation={askForPricingQuotation}
          isOpenAskInquiry={isOpenAskInquiry}
          setIsOpenAskInquiry={setIsOpenAskInquiry}
          isFromMongoDB={!!id && !ali}
          shouldShowWeightMessage={shouldShowWeightMessage}
          // hasZeroDimensions={hasZeroDimensions}
          onVariantUrlUpdate={handleVariantUrlUpdate}
          initialVariantId={initialVariantId}
          singleVariantPriceForTop={singleVariantPriceForTop}
          showSingleVariantPriceOnTop={showSingleVariantPriceOnTop}
          hasVariantsWithPriceAndDimensions={hasVariantsWithPriceAndDimensions}
          pricingData={pricingData}
        />

        <OrderSummary
          isLoading={pdLoading}
          selectedVariants={selectedVariants}
          pricingMode={pricingMode}
          isWholesale={isWholesale}
          onAddToCart={addToCart}
          onBuyNow={buyNow}
          product={product}
          searchKeyword={searchParams.get('search') || ''}
          isLoggedIn={isLoggedIn}
          authToken={authData.loginHeader}
          priceRanges={priceRanges}
          variantPrices={variantPrices}
          variantDetails={variantDetails}
          minVariantPrice={minVariantPrice}
          isDbProduct={isDbProduct}
          totalWholesalePrice={totalWholesalePrice}
          isMinMaxRange={isMinMaxRange}
          pricingData={pricingData}
          loadingPricing={loadingPricing}
          hasFetchedPricing={hasFetchedPricing}
          autoUpdating={autoUpdating}
          onFetchWholesalePricing={fetchWholesalePricing}
          askForPricingQuotation={askForPricingQuotation}
          setIsOpenAskInquiry={setIsOpenAskInquiry}
          shouldShowWeightMessage={shouldShowWeightMessage}
          // hasZeroDimensions={hasZeroDimensions}
          hasPriceLogs={hasPriceLogs}
          dbVariantPrices={dbVariantPrices}
        />
      </div>
    </div>
  );
};

export default ProductDetails;
